import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { continueNavigation } from '@shared/header_navigation'
import { useRoutes } from 'app/use_routes'
import { BookingContinueContext, type BookingContinueRegistration } from './booking_continue_context'
import { runContinueGuards, validateContinueGuards } from './run_continue_guards'
import type { BookingContinuePolicy } from './types'

type ActiveAttempt = {
  // Snapshot used to prevent an async completion from navigating after the attempt has been invalidated.
  generation: number
  // The complete page policy captured when this attempt began. It is also used to cancel pending guards.
  policy?: BookingContinuePolicy
  // Shared by racing callers so header and footer clicks join one continuation attempt.
  promise: Promise<void>
  // Current-state changes may be produced legitimately by continueNavigation, so they only cancel during guard execution.
  stage: 'guards' | 'navigation'
}

/**
 * Owns the single Continue workflow shared by the booking header and page footer.
 *
 * The mounted route page registers one ordered guard policy. Each call to continueBooking joins the active attempt or
 * runs that policy from the first guard, then calls continueNavigation exactly once when every guard allows it.
 * Route changes, booking resets, policy-owner changes, and unmounts invalidate the attempt and cancel pending guards.
 */
export const BookingContinueProvider = ({ children }: { children: ReactNode }) => {
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const location = useLocation()
  const { accountName = '', serviceTypeName = bookingCtx.state.serviceTypeName } = useParams()
  const registrationRef = useRef<BookingContinueRegistration>()
  const attemptRef = useRef<ActiveAttempt>()
  const generationRef = useRef(0)
  const pathnameRef = useRef(location.pathname)
  const cartRef = useRef(bookingCtx.state.cart)
  const mountedRef = useRef(true)
  const [isContinuing, setIsContinuing] = useState(false)

  // Modal-backed guards use cancel to settle any pending decision with false.
  const cancelPolicy = useCallback((policy?: BookingContinuePolicy) => {
    policy?.guards.forEach((guard) => guard.cancel?.())
  }, [])

  const registerPolicy = useCallback(
    (registration: BookingContinueRegistration) => {
      // A route policy replaces the previous policy as one unit, never as independently registered guards.
      const previous = registrationRef.current
      if (previous && previous !== registration) {
        const previousPolicy = previous.getPolicy()
        if (previousPolicy.owner !== registration.owner) {
          cancelPolicy(previousPolicy)
          if (attemptRef.current?.stage !== 'navigation') {
            generationRef.current += 1
          }
        }
      }

      registrationRef.current = registration

      return () => {
        if (registrationRef.current !== registration) return

        registrationRef.current = undefined
        cancelPolicy(registration.getPolicy())
        if (attemptRef.current?.stage !== 'navigation') {
          generationRef.current += 1
        }
      }
    },
    [cancelPolicy]
  )

  useLayoutEffect(() => {
    // A policy is executable only while its declared owner is the current booking page.
    const registration = registrationRef.current
    if (registration && registration.owner !== bookingCtx.state.currentState) {
      registrationRef.current = undefined
      cancelPolicy(registration.getPolicy())
      if (attemptRef.current?.stage === 'guards') {
        generationRef.current += 1
      }
    }
  }, [bookingCtx.state.currentState, cancelPolicy])

  useLayoutEffect(() => {
    // Browser or application navigation invalidates async work still owned by the previous route.
    if (pathnameRef.current === location.pathname) return

    pathnameRef.current = location.pathname
    generationRef.current += 1
    cancelPolicy(attemptRef.current?.policy)
  }, [cancelPolicy, location.pathname])

  useLayoutEffect(() => {
    // Selection changes invalidate guards already checking the old cart; an empty cart also identifies a booking reset.
    if (cartRef.current === bookingCtx.state.cart) return

    cartRef.current = bookingCtx.state.cart
    const attempt = attemptRef.current
    if (!attempt) return

    const bookingWasReset = bookingCtx.state.cart.groups.length === 0
    if (attempt.stage === 'guards' || bookingWasReset) {
      generationRef.current += 1
      cancelPolicy(attempt.policy)
    }
  }, [bookingCtx.state.cart, cancelPolicy])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      cancelPolicy(attemptRef.current?.policy)
      cancelPolicy(registrationRef.current?.getPolicy())
    }
  }, [cancelPolicy])

  const continueBooking = useCallback((): Promise<void> => {
    // Joining the same promise prevents racing header/footer clicks from duplicating checks or navigation.
    if (attemptRef.current) {
      return attemptRef.current.promise
    }

    const registration = registrationRef.current
    // Pages without a current matching policy intentionally continue with an empty guard list.
    const policy = registration?.owner === bookingCtx.state.currentState ? registration.getPolicy() : undefined
    if (policy) validateContinueGuards(policy.guards)

    const generation = generationRef.current
    setIsContinuing(true)

    const attempt: ActiveAttempt = {
      generation,
      policy,
      promise: Promise.resolve(),
      stage: 'guards',
    }
    const promise = (async () => {
      try {
        const canContinue = policy ? await runContinueGuards(policy.guards) : true
        if (!canContinue || generation !== generationRef.current || !mountedRef.current) return
        if (policy && policy.owner !== bookingCtx.state.currentState) return

        // From this point, continueNavigation may legitimately update currentState while it determines the next route.
        if (attemptRef.current) attemptRef.current.stage = 'navigation'
        const nextRoute = await continueNavigation(bookingCtx, accountName)
        if (!nextRoute || generation !== generationRef.current || !mountedRef.current) return

        navigateRoute(nextRoute, { serviceTypeName })
      } finally {
        if (attemptRef.current === attempt) {
          attemptRef.current = undefined
        }
        if (mountedRef.current) setIsContinuing(false)
      }
    })()

    attempt.promise = promise
    attemptRef.current = attempt
    return promise
  }, [accountName, bookingCtx, navigateRoute, serviceTypeName])

  const value = useMemo(
    () => ({ continueBooking, isContinuing, registerPolicy }),
    [continueBooking, isContinuing, registerPolicy]
  )

  return <BookingContinueContext.Provider value={value}>{children}</BookingContinueContext.Provider>
}
